import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BoardColumn, OrderAction, OrderBoardResponse, OrderDTO, OrderSummaryDTO } from '@scan2eat/shared';
import { ApiError, apiFetch, getAccessToken } from '../api/client.js';
import { useRealtimeSync } from '../realtime/useRealtimeSync.js';
import { useAuth } from '../auth/context.js';
import { ErrorState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { OrderCard } from '../components/OrderCard.js';
import { OrderDetailPanel } from '../components/OrderDetailPanel.js';
import { RejectPaymentDialog } from '../components/RejectPaymentDialog.js';
import { publishBoardCounts } from '../lib/board-counts.js';
import { placeOrderInColumns } from '../lib/board.js';
import { labels } from '../labels.js';
import styles from './BoardPage.module.css';

const STALE_CODES = new Set(['ORDER_INVALID_TRANSITION', 'ORDER_IMMUTABLE', 'NOT_FOUND']);
const BOARD_COLUMNS = ['NEW', 'IN_PROGRESS', 'READY', 'SERVED', 'COMPLETED'] as const;

/** Column accent: the status the column mostly holds. Always next to its written label. */
const COLUMN_TONE: Record<BoardColumn, string> = {
  NEW: 'pending',
  IN_PROGRESS: 'preparing',
  READY: 'ready',
  SERVED: 'served',
  COMPLETED: 'neutral',
};

export function BoardPage() {
  const { auth } = useAuth();
  const [board, setBoard] = useState<OrderBoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('emenu.soundEnabled') !== 'false'; } catch { return true; }
  });
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<OrderSummaryDTO | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [flashIds, setFlashIds] = useState<Set<string>>(() => new Set());
  const [banner, setBanner] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const boardRef = useRef<OrderBoardResponse | null>(null);
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;
  const bannerTimer = useRef<number | null>(null);

  const load = useCallback(() => {
    return apiFetch<OrderBoardResponse>('/admin/orders/board')
      .then((d) => { boardRef.current = d; setBoard(d); setLoadError(false); })
      .catch(() => { if (!boardRef.current) setLoadError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The sidebar counter reads the counts this page already holds.
  useEffect(() => {
    if (board) publishBoardCounts(board.counts);
  }, [board]);

  const { socket, connected } = useRealtimeSync(auth ? getAccessToken() : null, load);

  useEffect(() => {
    if (!socket) return;
    const applyOrder = (o: OrderSummaryDTO) => {
      setBoard((prev) => {
        if (!prev) return prev;
        const columns = placeOrderInColumns(prev.columns, o);
        return { ...prev, columns, counts: recount(columns) };
      });
    };
    const onCreated = (e: { payload: { order: OrderDTO } }) => {
      // docs/05 : `order:created` transporte un OrderDTO complet, tandis que
      // `order:status_changed` transporte deja un resume. Le kanban manipule
      // des resumes : on derive celui-ci au lieu de lire `itemsPreview`, qui
      // n'existe pas sur un OrderDTO. Le lire directement levait une TypeError
      // en plein rendu, ce qui demontait tout le back-office a la premiere
      // commande passee par un client.
      const { items, ...rest } = e.payload.order;
      const summary: OrderSummaryDTO = {
        ...rest,
        itemsPreview: items.slice(0, 3).map((item) => ({
          nameSnapshot: item.nameSnapshot,
          quantity: item.quantity,
        })),
      };
      applyOrder(summary);
      const id = summary.id;
      setFlashIds((prev) => new Set(prev).add(id));
      window.setTimeout(() => { setFlashIds((prev) => { const next = new Set(prev); next.delete(id); return next; }); }, 2500);
      setAnnouncement(labels.board.newOrderAnnounced(summary.number));
      if (soundRef.current) playNotification();
    };
    const onChanged = (e: { payload: { order: OrderSummaryDTO } }) => applyOrder(e.payload.order);
    // Un convive vient d'annoncer son paiement : la carte doit porter la
    // pastille d'attente sans que le personnel rafraichisse. Voir docs/05.
    const onPayment = () => { void load(); };
    socket.on('order:created' as never, onCreated as never);
    socket.on('order:status_changed' as never, onChanged as never);
    socket.on('payment:declared' as never, onPayment as never);
    socket.on('payment:verified' as never, onPayment as never);
    return () => {
      socket.off('order:created' as never, onCreated as never);
      socket.off('order:status_changed' as never, onChanged as never);
      socket.off('payment:declared' as never, onPayment as never);
      socket.off('payment:verified' as never, onPayment as never);
    };
  }, [socket, load]);

  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  useEffect(() => () => { if (bannerTimer.current) window.clearTimeout(bannerTimer.current); }, []);

  const showBanner = useCallback((message: string) => {
    setBanner(message);
    if (bannerTimer.current) window.clearTimeout(bannerTimer.current);
    bannerTimer.current = window.setTimeout(() => setBanner(null), 6000);
  }, []);

  const transition = useCallback(async (orderId: string, action: OrderAction, note?: string) => {
    try {
      await apiFetch(`/admin/orders/${orderId}/transition`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note ? { action, note } : { action }),
      });
    } catch (e) {
      if (e instanceof ApiError && STALE_CODES.has(e.code)) showBanner(labels.errors.staleTransition);
      else showBanner(labels.errors.generic);
    }
    await load();
  }, [load, showBanner]);

  /**
   * Rend le verdict sur un paiement declare par un convive.
   *
   * Confirmer termine la commande, emet la facture et ferme la session du
   * convive. Rejeter la laisse servie : le convive peut ressaisir sa
   * reference. Voir docs/06, section 10.
   */
  const verifyPayment = useCallback(async (
    order: OrderSummaryDTO,
    decision: 'CONFIRM' | 'REJECT',
    reason?: string,
  ) => {
    if (!order.payment) return;
    try {
      await apiFetch(`/admin/payments/${order.payment.id}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason ? { decision, reason } : { decision }),
      });
    } catch (e) {
      showBanner(e instanceof ApiError ? e.message : labels.errors.generic);
    }
    await load();
  }, [load, showBanner]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem('emenu.soundEnabled', String(next)); } catch { /* noop */ }
  };

  const activeCount = board
    ? board.counts.NEW + board.counts.IN_PROGRESS + board.counts.READY + board.counts.SERVED
    : 0;

  return (
    <div className={styles.page}>
      <div aria-live="assertive" className="visually-hidden">{announcement}</div>

      <header className={styles.header}>
        <div>
          <h1 className="page-title">{labels.board.title}</h1>
          <p className="page-subtitle">
            {board
              ? activeCount > 0
                ? labels.board.subtitle(auth?.user.firstName ?? '', activeCount)
                : labels.board.empty
              : labels.board.greeting(auth?.user.firstName ?? '')}
          </p>
        </div>

        <div className={styles.toolbar}>
          <span
            className={`badge badge--dot ${connected ? 'badge--ready' : 'badge--hold'}`}
            role="status"
          >
            {connected ? labels.board.online : labels.board.reconnecting}
          </span>
          <button
            type="button"
            onClick={toggleSound}
            className="btn-ghost btn-sm"
            aria-pressed={soundOn}
          >
            <Icon name={soundOn ? 'volume' : 'volume-off'} size={16} />
            {soundOn ? labels.board.soundOn : labels.board.soundOff}
          </button>
          <button type="button" onClick={load} className="btn-ghost btn-sm">
            <Icon name="refresh" size={16} />
            {labels.board.refresh}
          </button>
        </div>
      </header>

      {banner && (
        <div role="alert" className={`notice notice--warning ${styles.banner}`}>
          <Icon name="info" size={18} />
          <span className="grow">{banner}</span>
          <button type="button" className="btn-icon btn-icon--quiet btn-icon--sm" onClick={() => setBanner(null)} aria-label={labels.common.close}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}

      {loadError ? (
        <ErrorState onRetry={load} />
      ) : (
        <div className={styles.board}>
          {BOARD_COLUMNS.map((col) => {
            const orders = board?.columns[col] ?? [];
            return (
              <section key={col} aria-label={labels.columns[col]} className={styles.column}>
                <header className={styles.columnHead}>
                  <span className={`${styles.columnDot} ${styles[`tone_${COLUMN_TONE[col]}`]}`} aria-hidden="true" />
                  <h2 className={styles.columnTitle}>{labels.columns[col]}</h2>
                  <span className="count">{loading ? '-' : board?.counts[col] ?? orders.length}</span>
                </header>

                <div className={styles.columnBody}>
                  {loading ? (
                    Array.from({ length: col === 'NEW' ? 3 : 2 }).map((_, i) => (
                      <div key={i} className="skeleton" style={{ height: '8.5rem', borderRadius: 'var(--radius-lg)' }} />
                    ))
                  ) : orders.length === 0 ? (
                    <p className={styles.columnEmpty}>{labels.board.columnEmpty[col]}</p>
                  ) : (
                    orders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        now={now}
                        flashing={flashIds.has(order.id)}
                        watchAge={col === 'NEW' || col === 'IN_PROGRESS'}
                        isDone={col === 'COMPLETED'}
                        onOpenDetail={() => setSelectedOrderId(order.id)}
                        onTransition={(action, note) => transition(order.id, action, note)}
                        onVerifyPayment={(decision) => {
                          if (decision === 'REJECT') setRejecting(order);
                          else void verifyPayment(order, decision);
                        }}
                      />
                    ))
                  )}
                  {col === 'COMPLETED' && orders.length > 0 && (
                    <Link to="/historique" className={`btn-link ${styles.seeAll}`}>
                      {labels.board.seeAll}
                      <Icon name="arrow-right" size={16} />
                    </Link>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {selectedOrderId && (
        <OrderDetailPanel
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onTransition={(id, action, note) => transition(id, action as OrderAction, note)}
        />
      )}

      {rejecting && (
        <RejectPaymentDialog
          onCancel={() => setRejecting(null)}
          onConfirm={(reason) => {
            const order = rejecting;
            setRejecting(null);
            void verifyPayment(order, 'REJECT', reason);
          }}
        />
      )}
    </div>
  );
}

function recount(columns: OrderBoardResponse['columns']): OrderBoardResponse['counts'] {
  return {
    NEW: columns.NEW.length,
    IN_PROGRESS: columns.IN_PROGRESS.length,
    READY: columns.READY.length,
    SERVED: columns.SERVED.length,
    COMPLETED: columns.COMPLETED.length,
  };
}

function playNotification() {
  try {
    const ctx = new AudioContext(); const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 800; gain.gain.value = 0.3;
    osc.start(); gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* noop */ }
}
